import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight, Trash2, Filter, FileSpreadsheet, Sparkles,
         Languages, X, Copy, Check, Upload, Bot, Image as ImageIcon } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { MARKET_CODE_TO_DISPLAY } from '../lib/constants';
import { MarketFlag } from '../lib/MarketFlag';

const DISPLAY_TO_CODE = { Sverige: 'SE', Norge: 'NO', Finland: 'FI', Tyskland: 'DE', Danmark: 'DK', Nederländerna: 'NL' };

const TYPE_LABELS = {
  'norce':          { icon: FileSpreadsheet, label: 'Norce Export' },
  'single':         { icon: Sparkles,        label: 'Generera' },
  'ab':             { icon: Sparkles,        label: 'A/B Generera' },
  'translate-text': { icon: Languages,       label: 'Översätt text' },
  'translate-file': { icon: Languages,       label: 'Översätt fil' },
  'autopilot':      { icon: Bot,             label: 'Autopilot' },
  'image-upload':   { icon: ImageIcon,       label: 'Bilduppladdning' },
  'image-remove':   { icon: Trash2,          label: 'Bildradering' },
};

const MODEL_LABELS = { gemini: 'Gemini 2.5 Flash', claude: 'Claude Sonnet 4.5', gpt4o: 'GPT-4o' };

function formatDuration(s) {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatDate(ts) {
  return new Date(ts).toLocaleDateString('sv-SE', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Resolve any market value (code or display name) → { display, code }
function resolveMarket(raw) {
  if (!raw) return { display: '', code: '' };
  const code    = DISPLAY_TO_CODE[raw] || raw;
  const display = MARKET_CODE_TO_DISPLAY[code] || raw;
  return { display, code };
}

// ── Autopilot detail panel content ────────────────────────────────────────────
const AP_STATUS_LABELS = {
  pending_review:    'Väntar granskning',
  published:         'Publicerad',
  partial_published: 'Delvis publicerad',
  publish_failed:    'Publicering misslyckades',
  rejected:          'Avvisad',
  generation_failed: 'Generering misslyckades',
  scraping_failed:   'Skrapning misslyckades',
};

function AutopilotDetailContent({ log }) {
  const od = log.outputData || {};
  const markets = Object.keys(od.generated_content || {});
  const [copiedField, setCopiedField] = useState(null);

  const copyField = (text, key) => {
    navigator.clipboard.writeText(text || '').catch(() => {});
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Collect source URLs
  const sourceUrls = useMemo(() => {
    const urls = new Set();
    Object.values(od.generated_content || {}).forEach(c => (c?.source_urls || []).forEach(u => urls.add(u)));
    return Array.from(urls).slice(0, 8);
  }, [od.generated_content]);

  return (
    <div className="log-rich-panel">
      <div className="log-rich-header">
        <h2 className="log-rich-product" data-testid="ap-log-product">{log.productName || '—'}</h2>
        <div className="log-rich-meta">
          <code style={{ fontSize: 12, color: '#64748b' }}>{od.part_no || ''}</code>
          {od.part_no && <span className="log-rich-sep">·</span>}
          <span className="log-rich-time">{formatDate(log.timestamp)}</span>
        </div>
        <div className="log-rich-chips">
          <span className={`ap-queue-status ap-queue-status--${log.status}`} style={{ fontSize: 12 }}>
            {AP_STATUS_LABELS[log.status] || log.status}
          </span>
          {(log.markets || []).map(m => {
            const { display, code } = resolveMarket(m);
            return <span key={m} className="log-rich-chip"><MarketFlag market={code || m} /> {display || m}</span>;
          })}
        </div>
      </div>

      {/* Bullets */}
      {od.bullets && (
        <div className="log-rich-section">
          <div className="log-rich-section-head">
            <span className="log-rich-section-label">BULLETS (FRÅN SKRAPNING)</span>
          </div>
          <pre className="log-rich-text" style={{ fontSize: 13 }}>{od.bullets}</pre>
        </div>
      )}

      {/* Source URLs */}
      {sourceUrls.length > 0 && (
        <div className="log-rich-section">
          <div className="log-rich-section-head">
            <span className="log-rich-section-label">KÄLLOR (TAVILY)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            {sourceUrls.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noopener noreferrer"
                style={{ color: '#0891b2', textDecoration: 'none', wordBreak: 'break-all' }}>
                {(() => { try { return new URL(u).hostname; } catch { return u.slice(0, 60); } })()}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Generated content per market */}
      {markets.map(mkt => {
        const c = (od.generated_content || {})[mkt] || {};
        if (c.error) return (
          <div key={mkt} className="log-rich-section">
            <div className="log-rich-section-head">
              <span className="log-rich-section-label"><MarketFlag market={mkt} /> {mkt} — FEL</span>
            </div>
            <p style={{ color: '#dc2626', fontSize: 13 }}>{c.error}</p>
          </div>
        );
        const { display, code } = resolveMarket(mkt);
        const isPub = (od.published_markets || []).includes(mkt);
        const hasErr = od.publish_errors?.[mkt];
        return (
          <div key={mkt} className="log-rich-section">
            <div className="log-rich-section-head">
              <span className="log-rich-section-label">
                <MarketFlag market={code || mkt} /> {display || mkt}
                {isPub && <span style={{ marginLeft: 6, color: '#16a34a', fontSize: 11 }}>✓ Publicerad</span>}
                {hasErr && <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 11 }}>✗ Fel</span>}
              </span>
              <button className="log-rich-copy-btn"
                onClick={() => copyField([c.description, c.meta_title && `Meta Title: ${c.meta_title}`, c.meta_description && `Meta Description: ${c.meta_description}`].filter(Boolean).join('\n\n'), mkt)}>
                {copiedField === mkt ? <><Check size={12} />Kopierat</> : <><Copy size={12} />Kopiera</>}
              </button>
            </div>
            {c.description && <pre className="log-rich-text">{c.description}</pre>}
            {c.meta_title && (
              <div style={{ marginTop: 8 }}>
                <span className="log-rich-section-label" style={{ fontSize: 10 }}>META TITLE</span>
                <p className="log-rich-value">{c.meta_title}</p>
              </div>
            )}
            {c.meta_description && (
              <div style={{ marginTop: 8 }}>
                <span className="log-rich-section-label" style={{ fontSize: 10 }}>META DESCRIPTION</span>
                <p className="log-rich-value">{c.meta_description}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Rich "Generera" detail panel ──────────────────────────────────────────────
function GenerateDetailContent({ log, od }) {
  const [partNo,       setPartNo]      = useState(od?.partNo || '');
  const [copiedField,  setCopiedField] = useState(null);
  const [norceStatus,  setNorceStatus] = useState('idle'); // idle|loading|done|error
  const [norceMessage, setNorceMessage] = useState('');

  const rawMarket = log.markets?.[0] || od?.market || '';
  const { display: marketDisplay, code: marketCode } = resolveMarket(rawMarket);

  const copyField = (text, key) => {
    navigator.clipboard.writeText(text || '').catch(() => {});
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyAll = () => {
    const parts = [];
    if (od.description)     parts.push(od.description);
    if (od.metaTitle)        parts.push(`Meta Title: ${od.metaTitle}`);
    if (od.metaDescription) parts.push(`Meta Description: ${od.metaDescription}`);
    navigator.clipboard.writeText(parts.join('\n\n')).catch(() => {});
    setCopiedField('all');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const publishToNorce = async () => {
    if (!partNo.trim()) return;
    setNorceStatus('loading');
    setNorceMessage('');
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiUrl}/api/norce/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: [{
          partNo: partNo.trim(),
          market: marketCode,
          description:     od.description     || '',
          metaTitle:       od.metaTitle       || '',
          metaDescription: od.metaDescription || '',
        }] }),
      });
      const data = await resp.json();
      const result = data.results?.[0];
      if (result?.success) {
        setNorceStatus('done');
      } else {
        setNorceStatus('error');
        setNorceMessage(result?.error || 'Okänt fel');
      }
    } catch (err) {
      setNorceStatus('error');
      setNorceMessage(err.message);
    }
  };

  return (
    <div className="log-rich-panel">
      {/* ── Rich header ───────────────────────────────────────────────────── */}
      <div className="log-rich-header">
        <h2 className="log-rich-product" data-testid="log-rich-product">{log.productName || '—'}</h2>
        <div className="log-rich-meta">
          {marketCode && <span className="log-rich-market"><MarketFlag market={marketCode} /> {marketDisplay}</span>}          {od.category && <><span className="log-rich-sep">·</span><span className="log-rich-cat">{od.category}</span></>}
          <span className="log-rich-sep">·</span>
          <span className="log-rich-time">{formatDate(log.timestamp)}</span>
        </div>
        <div className="log-rich-chips">
          {od.model    && <span className="log-rich-chip log-rich-chip--model">{MODEL_LABELS[od.model] || od.model}</span>}
          {od.wordCount && <span className="log-rich-chip log-rich-chip--words">{od.wordCount} ord</span>}
          {log.durationSeconds && <span className="log-rich-chip">{formatDuration(log.durationSeconds)}</span>}
        </div>
      </div>

      {/* ── Produktbeskrivning ─────────────────────────────────────────────── */}
      {od.description && (
        <div className="log-rich-section">
          <div className="log-rich-section-head">
            <span className="log-rich-section-label">PRODUKTBESKRIVNING</span>
            <button className="log-rich-copy-btn" data-testid="log-copy-desc"
              onClick={() => copyField(od.description, 'desc')}>
              {copiedField === 'desc' ? <><Check size={12} />Kopierat</> : <><Copy size={12} />Kopiera</>}
            </button>
          </div>
          <pre className="log-rich-text">{od.description}</pre>
        </div>
      )}

      {/* ── Meta Title ────────────────────────────────────────────────────── */}
      {od.metaTitle && (
        <div className="log-rich-section">
          <div className="log-rich-section-head">
            <span className="log-rich-section-label">META TITLE</span>
            <span className="log-rich-char-count">{od.metaTitle.length} tecken</span>
            <button className="log-rich-copy-btn" data-testid="log-copy-title"
              onClick={() => copyField(od.metaTitle, 'title')}>
              {copiedField === 'title' ? <><Check size={12} />Kopierat</> : <><Copy size={12} />Kopiera</>}
            </button>
          </div>
          <p className="log-rich-value">{od.metaTitle}</p>
        </div>
      )}

      {/* ── Meta Description ──────────────────────────────────────────────── */}
      {od.metaDescription && (
        <div className="log-rich-section">
          <div className="log-rich-section-head">
            <span className="log-rich-section-label">META DESCRIPTION</span>
            <span className="log-rich-char-count">{od.metaDescription.length} tecken</span>
            <button className="log-rich-copy-btn" data-testid="log-copy-metadesc"
              onClick={() => copyField(od.metaDescription, 'metadesc')}>
              {copiedField === 'metadesc' ? <><Check size={12} />Kopierat</> : <><Copy size={12} />Kopiera</>}
            </button>
          </div>
          <p className="log-rich-value">{od.metaDescription}</p>
        </div>
      )}

      {/* ── Actions ───────────────────────────────────────────────────────── */}
      <div className="log-rich-actions">
        <button className="log-rich-action-btn" data-testid="log-copy-all" onClick={copyAll}>
          {copiedField === 'all' ? <><Check size={14} />Kopierat!</> : <><Copy size={14} />Kopiera alla fält</>}
        </button>

        <div className="log-rich-norce-row">
          {!od.partNo && (
            <input
              type="text"
              className="log-rich-partno-input"
              placeholder="Artikelnummer (PartNo)"
              value={partNo}
              onChange={e => setPartNo(e.target.value)}
              data-testid="log-partno-input"
            />
          )}
          <button
            className={`log-rich-norce-btn${norceStatus === 'done' ? ' log-rich-norce-btn--done' : norceStatus === 'error' ? ' log-rich-norce-btn--error' : ''}`}
            onClick={publishToNorce}
            disabled={!partNo.trim() || norceStatus === 'loading' || norceStatus === 'done'}
            data-testid="log-publish-norce-btn"
          >
            {norceStatus === 'loading' ? <><span className="spinner-sm" />Publicerar...</> :
             norceStatus === 'done'    ? <><Check size={14} />Publicerad!</> :
                                         <><Upload size={14} />Publicera till Norce</>}
          </button>
        </div>
        {norceStatus === 'error' && (
          <p className="log-rich-norce-error" data-testid="log-norce-error">{norceMessage}</p>
        )}
      </div>
    </div>
  );
}

// ── Detail side panel ─────────────────────────────────────────────────────────
function LogDetailPanel({ log, onClose }) {
  const [copiedTrField, setCopiedTrField] = useState(null);
  if (!log) return null;
  const od = log.outputData;
  const typeInfo = TYPE_LABELS[log.type] || { icon: Sparkles, label: log.type };
  const isGenerateType = log.type === 'single' || log.type === 'ab';
  const isTranslateText = log.type === 'translate-text';
  const isTranslateFile = log.type === 'translate-file';
  const isAutopilot = log.type === 'autopilot';

  const copyTrField = (text, key) => {
    navigator.clipboard.writeText(text || '').catch(() => {});
    setCopiedTrField(key);
    setTimeout(() => setCopiedTrField(null), 2000);
  };

  return (
    <>
      <div className="log-panel-overlay" onClick={onClose} />
      <aside className="log-detail-panel" data-testid="log-detail-panel">

        {/* ── Panel header (always visible) ───────────────────────────────── */}
        <div className="log-panel-header">
          <span className="log-panel-title">{typeInfo.label} — {log.productName || log.fileName || '—'}</span>
          <button className="log-panel-close" onClick={onClose} data-testid="log-panel-close">
            <X size={16} />
          </button>
        </div>

        {/* ── Autopilot type → rich panel ────────────────────────────────── */}
        {isAutopilot ? (
          <AutopilotDetailContent log={log} />
        ) : isGenerateType && od ? (
          <GenerateDetailContent log={log} od={od} />
        ) : (
          /* ── All other types / missing data ──────────────────────────────── */
          <>
            <div className="log-panel-meta">
              <span>{formatDate(log.timestamp)}</span>
              {(log.markets || []).map(m => {
                const { display, code } = resolveMarket(m);
                return <span key={m} className="log-market-flag" title={display || m}><MarketFlag market={code || m} /></span>;
              })}
              {log.durationSeconds && <span>{formatDuration(log.durationSeconds)}</span>}
            </div>

            {/* Per-product table */}
            {log.perProduct?.length > 0 && (
              <div className="log-panel-products" data-testid="log-panel-products">
                <div className="log-panel-products-header">PRODUKTER ({log.perProduct.length})</div>
                <table className="log-detail-table">
                  <thead>
                    <tr><th>PRODUKT</th><th>MARKNAD</th><th>ORD</th><th>STATUS</th></tr>
                  </thead>
                  <tbody>
                    {log.perProduct.map((p, i) => {
                      const { display, code } = resolveMarket(p.market);
                      return (
                        <tr key={`${p.market}-${i}`} className={p.status === 'error' ? 'log-detail-error' : ''}>
                          <td>{p.name || p.partNo || '—'}</td>
                          <td><MarketFlag market={code || p.market} /> {display || p.market}</td>
                          <td>{p.wordCount ?? '—'}</td>
                          <td>{p.status === 'ok' ? '✓' : p.error || 'Fel'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {od ? (
              <div className="log-panel-content">
                {/* Translate text — per-market sections with copy */}
                {isTranslateText && (
                  <>
                    {od.sourceText && (
                      <div className="log-panel-field">
                        <span className="log-panel-label">Källtext</span>
                        <pre className="log-panel-text">{od.sourceText}</pre>
                      </div>
                    )}
                    {od.translations && Object.entries(od.translations).map(([mkt, text]) => {
                      const { display, code } = resolveMarket(mkt);
                      return (
                        <div key={mkt} className="log-rich-section">
                          <div className="log-rich-section-head">
                            <span className="log-rich-section-label"><MarketFlag market={code || mkt} /> {display || mkt}</span>
                            <button className="log-rich-copy-btn"
                              onClick={() => copyTrField(text, mkt)}>
                              {copiedTrField === mkt ? <><Check size={12} />Kopierat</> : <><Copy size={12} />Kopiera</>}
                            </button>
                          </div>
                          <pre className="log-rich-text">{text}</pre>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Translate file */}
                {isTranslateFile && (
                  <div className="log-panel-field">
                    <span className="log-panel-label">Fil</span>
                    <span className="log-panel-value">{log.fileName || '—'}</span>
                  </div>
                )}

                {/* Generate type without outputData content → compact message */}
                {isGenerateType && (
                  <div className="log-panel-empty-compact">
                    <p className="log-panel-empty-compact-text">Detaljdata saknas för denna loggpost.</p>
                    <p className="log-panel-empty-compact-sub">Framtida genereringar sparar fullständigt innehåll automatiskt.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="log-panel-empty-compact">
                <p className="log-panel-empty-compact-text">Detaljdata saknas för denna loggpost.</p>
                <p className="log-panel-empty-compact-sub">Framtida genereringar sparar fullständigt innehåll automatiskt.</p>
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
}

function LogRow({ log, onSelect }) {
  const typeInfo = TYPE_LABELS[log.type] || { icon: Sparkles, label: log.type };
  const Icon = typeInfo.icon;

  return (
    <tr
      className="log-row log-row--clickable"
      onClick={() => onSelect(log)}
      data-testid={`log-row-${log.id}`}
    >
      <td className="log-cell-expand">
        <ChevronRight size={14} className="log-row-chevron" />
      </td>
      <td className="log-cell-date">{formatDate(log.timestamp)}</td>
      <td>
        <span className="log-type-badge">
          <Icon size={12} />
          {typeInfo.label}
        </span>
      </td>
      <td className="log-cell-name" title={log.fileName || log.productName || '—'}>
        {log.fileName || log.productName || '—'}
      </td>
      <td className="log-cell-markets">
        {(log.markets || []).map(m => {
          const { display, code } = resolveMarket(m);
          return <span key={m} className="log-market-flag" title={display || m}><MarketFlag market={code || m} /></span>;
        })}
      </td>
      <td className="log-cell-num">{log.products ?? 1}</td>
      <td className="log-cell-num log-ok">{log.success ?? (log.status === 'ok' ? 1 : 0)}</td>
      <td className="log-cell-num log-fail">{log.failed ?? (log.status === 'error' ? 1 : 0)}</td>
      <td className="log-cell-duration">{formatDuration(log.durationSeconds)}</td>
    </tr>
  );
}

export default function LogsPage() {
  const { logs, clearLogs } = useData();
  const { user } = useAuth() || {};
  const [filterMarket, setFilterMarket] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [autopilotLogs, setAutopilotLogs] = useState([]);
  const [imageLogs, setImageLogs] = useState([]);

  // Fetch upload_logs (image manager) from Supabase
  useEffect(() => {
    if (!user || !supabase) return;
    supabase
      .from('upload_logs')
      .select('id, user_email, product_name, part_no, status, image_count, error_message, norce_job_id, duration_ms, created_at')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (error || !data) return;
        setImageLogs(data.map(row => {
          const isRemove = row.status === 'removed';
          const success = ['success', 'removed'].includes(row.status);
          return {
            id: `img-${row.id}`,
            type: isRemove ? 'image-remove' : 'image-upload',
            timestamp: row.created_at,
            productName: row.product_name || row.part_no || '—',
            fileName: row.part_no,
            markets: ['SE', 'NO', 'FI', 'DK', 'DE', 'NL'],
            status: success ? 'ok' : 'error',
            products: 1,
            success: success ? 1 : 0,
            failed: success ? 0 : 1,
            durationSeconds: row.duration_ms ? Math.max(1, Math.round(row.duration_ms / 1000)) : null,
            outputData: {
              part_no: row.part_no,
              image_count: row.image_count,
              norce_job_id: row.norce_job_id,
              error_message: row.error_message,
              user_email: row.user_email,
            },
          };
        }));
      });
  }, [user]);

  // Fetch autopilot entries from pipeline_queue
  useEffect(() => {
    if (!user || !supabase) return;
    supabase
      .from('pipeline_queue')
      .select('id, part_no, product_name, markets, status, created_at, bullets, generated_content, published_markets, publish_errors')
      .eq('user_id', user.id)
      .in('status', ['pending_review', 'published', 'partial_published', 'publish_failed', 'generation_failed', 'scraping_failed', 'rejected'])
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (!data) return;
        setAutopilotLogs(data.map(row => ({
          id: `ap-${row.id}`,
          type: 'autopilot',
          timestamp: row.created_at,
          productName: row.product_name || '—',
          markets: row.markets || [],
          status: row.status,
          products: 1,
          success: ['published', 'partial_published', 'pending_review'].includes(row.status) ? 1 : 0,
          failed: ['publish_failed', 'generation_failed', 'scraping_failed'].includes(row.status) ? 1 : 0,
          outputData: {
            part_no: row.part_no,
            bullets: row.bullets,
            generated_content: row.generated_content || {},
            published_markets: row.published_markets || [],
            publish_errors: row.publish_errors || {},
          },
        })));
      });
  }, [user]);

  // Merge logs + autopilot + image, sorted by timestamp desc
  const allLogs = useMemo(() => {
    return [...logs, ...autopilotLogs, ...imageLogs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [logs, autopilotLogs, imageLogs]);

  const filtered = useMemo(() => {
    return allLogs.filter(log => {
      if (filterMarket && !log.markets?.includes(filterMarket)) return false;
      if (filterDate) {
        const logDate = new Date(log.timestamp).toISOString().slice(0, 10);
        if (logDate < filterDate) return false;
      }
      return true;
    });
  }, [allLogs, filterMarket, filterDate]);

  const handleClear = () => {
    if (window.confirm('Rensa alla loggar?')) clearLogs();
  };

  return (
    <div className="page-scroll" data-testid="logs-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Loggar</h1>
          <p className="page-subtitle">{allLogs.length} post{allLogs.length !== 1 ? 'er' : ''} registrerade. Klicka på en rad för detaljer.</p>
        </div>
        {logs.length > 0 && (
          <button className="vocab-btn vocab-btn--ghost vocab-btn--danger" onClick={handleClear} data-testid="clear-logs-btn">
            <Trash2 size={14} /> Rensa loggar
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="vocab-filters" data-testid="log-filters">
        <div className="vocab-select-wrap">
          <select className="vocab-filter-select" value={filterMarket} onChange={e => setFilterMarket(e.target.value)} data-testid="filter-market">
            <option value="">Alla marknader</option>
            {['SE','NO','FI','DE','DK','NL'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <ChevronDown size={13} className="vocab-select-icon" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={13} style={{ color: '#9ca3af' }} />
          <input type="date" className="vocab-filter-select" value={filterDate}
            onChange={e => setFilterDate(e.target.value)} data-testid="filter-date"
            style={{ paddingRight: 10 }} />
        </div>
        <span className="vocab-count">{filtered.length} poster</span>
        {(filterMarket || filterDate) && (
          <button className="translate-select-all-btn" onClick={() => { setFilterMarket(''); setFilterDate(''); }}>
            Rensa filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="vocab-table-wrap">
        <table className="log-table" data-testid="logs-table">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Tid</th>
              <th>Typ</th>
              <th>Fil / Produkt</th>
              <th>Marknader</th>
              <th title="Antal produkter">Prod.</th>
              <th title="Lyckade" className="log-ok">OK</th>
              <th title="Misslyckade" className="log-fail">Fel</th>
              <th>Tid</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="vocab-empty">
                {allLogs.length === 0 ? 'Inga loggar ännu — kör en generering eller autopilot för att börja.' : 'Inga loggar matchar filtret.'}
              </td></tr>
            )}
            {filtered.map(log => (
              <LogRow key={log.id} log={log} onSelect={setSelectedLog} />
            ))}
          </tbody>
        </table>
      </div>

      {selectedLog && (
        <LogDetailPanel log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}
